// Small pieces the structure screens (seats, metrics and targets, publications, the import
// wizard, numbers) share: problem text, the inline error notice, a confirm-before-acting
// button on `AlertDialog`, the read-only notice and a paged table's "Load more". Composition
// of @companyos/ui only; no styling of its own.
import { ApiError } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Notice,
} from "@companyos/ui";
import { type ReactNode, useState } from "react";

/** The problem's detail (or its first field error), else the error's message. */
export function problemText(error: unknown): string {
  if (error instanceof ApiError) {
    const p = error.problem;
    const field = p?.errors?.[0];
    return (
      p?.detail ??
      (field ? `${field.path}: ${field.message}` : undefined) ??
      p?.title ??
      error.message
    );
  }
  return error instanceof Error ? error.message : "Unexpected error.";
}

/** The problem code of an API error (`seat_occupied`, `already_published`, …), if any. */
export function problemCode(error: unknown): string | undefined {
  return error instanceof ApiError ? error.problem?.code : undefined;
}

/** A failed load, inline (the toast already carries the problem). */
export function ErrorNotice({ what, error }: { what: string; error: unknown }) {
  return (
    <Notice tone="danger" role="alert" title={`${what} could not be loaded`}>
      {problemText(error)}
    </Notice>
  );
}

/** A failed write, inline under the form that sent it. */
export function FormError({ error, what }: { error: unknown; what: string }) {
  if (!error) return null;
  return (
    <Notice tone="danger" role="alert" title={`${what} failed`}>
      {problemText(error)}
    </Notice>
  );
}

/** Why the page's writes are off (the add-on's grace period). */
export function ReadOnlyNotice({ what }: { what: string }) {
  return (
    <Notice tone="warning" title="Read-only" data-slot="read-only">
      The KPI add-on is in its grace period: {what} can be read, not changed.
    </Notice>
  );
}

/**
 * A button that asks first (`AlertDialog`): destructive actions (close a seat, archive a
 * metric, delete a target, remove a value, reverse an import) never run on one click.
 */
export function ConfirmButton({
  label,
  title,
  description,
  confirmLabel,
  onConfirm,
  pending = false,
  disabled = false,
  size = "sm",
  variant = "secondary",
  destructive = true,
  srLabel,
}: {
  label: ReactNode;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  onConfirm: () => Promise<unknown> | unknown;
  pending?: boolean;
  disabled?: boolean;
  size?: "sm" | "default" | "lg";
  variant?: "secondary" | "ghost" | "destructive";
  destructive?: boolean;
  /** Names the subject for screen readers when the label is short ("Delete"). */
  srLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger
        render={<Button size={size} variant={variant} disabled={disabled || pending} />}
      >
        {label}
        {srLabel ? <span className="sr-only"> {srLabel}</span> : null}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant={destructive ? "destructive" : "default"}
            disabled={pending}
            onClick={async () => {
              try {
                await onConfirm();
              } catch {
                // The problem toast reports it; the dialog closes either way.
              } finally {
                setOpen(false);
              }
            }}
          >
            {pending ? "Working…" : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** "Load more" under a cursor-paged table. */
export function MoreRows({
  what,
  q,
}: {
  what: string;
  q: { hasNextPage: boolean; isFetchingNextPage: boolean; fetchNextPage: () => unknown };
}) {
  if (!q.hasNextPage) return null;
  return (
    <Button
      variant="secondary"
      size="sm"
      className="mt-3 self-start"
      disabled={q.isFetchingNextPage}
      onClick={() => void q.fetchNextPage()}
    >
      {q.isFetchingNextPage ? "Loading…" : `Load more ${what}`}
    </Button>
  );
}
